import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight, PenLine } from "lucide-react";
import { Reveal } from "../components/motion";
import { Seo } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog`).then(({ data }) => setPosts(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Yuva Kumar — Journal",
    description: "Field notes on UX leadership, design systems governance and AI-native product design.",
    url: "https://yuvadesign.in/blog",
    blogPost: posts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      description: p.excerpt,
      datePublished: p.created_at,
      author: { "@type": "Person", name: p.author || "Yuva Kumar" },
      url: `https://yuvadesign.in/blog/${p.slug}`,
    })),
  };

  return (
    <div data-testid="blog-page">
      <Seo
        path="/blog"
        description="Journal by Yuva Kumar — field notes on UX leadership, design systems governance, design ops and AI-native product design from 20+ years scaling design across APAC."
        keywords="UX leadership blog, design systems, AI-native design, design ops, Yuva Kumar journal"
        jsonLd={jsonLd}
      />

      <section className="pt-40 pb-16">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <Reveal><p className="eyebrow mb-6">Journal</p></Reveal>
              <Reveal delay={0.1}>
                <h1 className="display text-5xl sm:text-8xl max-w-[16ch]" style={{ color: "var(--text)" }}>Notes on design & leadership.</h1>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <Link to="/blog/new" data-testid="write-post-link" className="group inline-flex items-center gap-2 px-6 py-3 rounded-full border text-sm uppercase tracking-widest" style={{ borderColor: "var(--line)", color: "var(--text)" }}>
                <PenLine size={16} style={{ color: "var(--accent)" }} /> Write
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-32">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10">
          {loading ? (
            <p style={{ color: "var(--muted-t)" }}>Loading…</p>
          ) : posts.length === 0 ? (
            <p style={{ color: "var(--muted-t)" }} data-testid="blog-empty">No posts yet. Be the first to publish.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((p, i) => (
                <Reveal key={p.slug} delay={(i % 3) * 0.08}>
                  <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group block h-full">
                    {p.cover_image ? (
                      <div className="overflow-hidden mb-5" style={{ borderRadius: "2px", border: "1px solid var(--line)" }}>
                        <div className="aspect-[16/10] overflow-hidden">
                          <img src={p.cover_image} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{ filter: "grayscale(0.25) contrast(1.05)" }} />
                        </div>
                      </div>
                    ) : null}
                    <div className="flex items-center gap-3 mb-3">
                      {p.tags?.[0] ? <span className="eyebrow">{p.tags[0]}</span> : null}
                      <span className="text-xs" style={{ color: "var(--muted-t)" }}>{p.read_minutes} min read</span>
                    </div>
                    <h2 className="display text-2xl sm:text-3xl group-hover:opacity-70 transition-opacity" style={{ color: "var(--text)" }}>{p.title}</h2>
                    <p className="text-sm mt-3 leading-relaxed line-clamp-3" style={{ color: "var(--muted-t)" }}>{p.excerpt}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-widest" style={{ color: "var(--accent)" }}>
                      Read <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
